
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import {
  HiOutlineArrowUpRight,
  HiOutlineScale,
} from "react-icons/hi2";

import { getAllServices } from "../../redux/actions/services";
import { Loader } from "../utils";
import ServiceImage from "./ServiceImage";

/* ============================================
   RELATED SERVICES SECTION
============================================ */

function RelatedServices({ currentServiceId }) {
  const dispatch = useDispatch();
  const { i18n } = useTranslation();

  const { isLoading, Allservices } = useSelector(
    (state) => state.Services || {}
  );

  const isEnglish = (
    i18n.resolvedLanguage ||
    i18n.language ||
    "ar"
  )
    .toLowerCase()
    .startsWith("en");

  useEffect(() => {
    dispatch(getAllServices(1, 6));
  }, [dispatch]);

  const services = Array.isArray(Allservices)
    ? Allservices
        .filter((service) =>
          currentServiceId
            ? String(service._id) !== String(currentServiceId)
            : true
        )
        .slice(0, 3)
    : [];

  const content = {
    eyebrow: isEnglish
      ? "EXPLORE MORE"
      : "اكتشف المزيد",

    title: isEnglish
      ? "Other Legal Services"
      : "خدمات قانونية أخرى",

    description: isEnglish
      ? "Explore additional practice areas offered by Al Noman Law Office."
      : "تعرف على مجالات قانونية أخرى تقدمها مؤسسة النعمان.",

    discover: isEnglish
      ? "Explore Service"
      : "تفاصيل الخدمة",

    all: isEnglish
      ? "View All Services"
      : "جميع الخدمات",
  };

  if (!isLoading && services.length === 0) {
    return null;
  }

  return (
    <section
      className="
        relative
        border-t border-[#DCE6ED]
        bg-[#EAF0F5]

        py-16
        md:py-20
        lg:py-24

        dark:border-white/10
        dark:bg-[#13293B]
      "
    >
      <div className="container">

        {/* ======================================
            SECTION HEADING
        ====================================== */}

        <div
          className="
            mb-11 flex flex-col
            justify-between gap-7

            md:flex-row
            md:items-end
          "
        >
          <div className="max-w-[730px]">

            <div className="mb-5 flex items-center gap-3">
              <span className="h-px w-9 bg-skin-accent" />

              <span className="eyebrow text-skin-accent">
                {content.eyebrow}
              </span>
            </div>

            <h2 className="section-title text-skin-heading">
              {content.title}
            </h2>

            <p className="body-2 mt-5 text-skin-muted">
              {content.description}
            </p>

          </div>

          {/* View all link */}
          <Link
            to="/AllServiceses"
            onClick={() => window.scrollTo(0, 0)}
            className="
              group inline-flex min-h-[48px]
              items-center justify-center
              gap-3 self-start

              rounded-xl
              border border-skin-border
              bg-skin-surface

              px-5 py-3
              text-skin-heading

              transition-all duration-300

              hover:border-skin-accent/40
              hover:bg-skin-soft
            "
          >
            <span className="button-sm">
              {content.all}
            </span>

            <HiOutlineArrowUpRight
              size={19}
              aria-hidden="true"
              className="directional-arrow text-skin-accent"
            />
          </Link>

        </div>

        {/* ======================================
            SERVICES GRID
        ====================================== */}

        {isLoading ? (
          <div className="flex min-h-[260px] items-center justify-center">
            <Loader />
          </div>
        ) : (
          <div
            className="
              grid grid-cols-1
              gap-6

              md:grid-cols-2
              lg:grid-cols-3
              lg:gap-7
            "
          >
            {services.map((service, index) => {
              const title = isEnglish
                ? service.titleEn || service.titleAr
                : service.titleAr || service.titleEn;

              const text = isEnglish
                ? service.textEn || service.textAr
                : service.textAr || service.textEn;

              const preview = String(text || "")
                .replace(/<[^>]*>/g, " ")
                .replace(/\s+/g, " ")
                .trim();

              return (
                <Link
                  key={service._id || index}
                  to="/ServiceDetails"
                  state={{ ServiceData: service }}
                  onClick={() => window.scrollTo(0, 0)}
                  className="
                    group flex h-full min-w-0
                    flex-col overflow-hidden

                    rounded-[22px]

                    border border-[#D9E4EC]
                    bg-white

                    shadow-[0_10px_35px_rgba(15,39,55,0.04)]

                    transition-all duration-300

                    hover:-translate-y-1
                    hover:border-skin-accent/40
                    hover:shadow-card

                    focus-visible:outline-none
                    focus-visible:ring-2
                    focus-visible:ring-skin-accent

                    dark:border-white/10
                    dark:bg-[#1C354A]
                    dark:shadow-none
                    dark:hover:border-[#D7B77F]/35
                  "
                >
                  {/* Image */}
                  <div className="relative overflow-hidden">
                    <ServiceImage
                      image={service.Image}
                      alt=""
                      className="aspect-[16/10] w-full"
                    />

                    {/* Dark gradient */}
                    <div
                      aria-hidden="true"
                      className="
                        pointer-events-none
                        absolute inset-0
                        bg-gradient-to-t
                        from-[#071522]/40
                        to-transparent
                      "
                    />

                    {/* Number */}
                    <span
                      className="
                        absolute end-4 top-4
                        rounded-lg
                        border border-white/15
                        bg-[#102031]/65
                        px-3 py-1.5

                        font-cairo text-xs
                        font-semibold tabular-nums
                        text-white
                        backdrop-blur-lg
                      "
                    >
                      {String(index + 1).padStart(2, "0")}
                    </span>
                  </div>

                  {/* Content */}
                  <div
                    className="
                      flex flex-1
                      flex-col
                      px-6 pb-6 pt-7
                    "
                  >
                    <div className="mb-4 flex items-center gap-3">
                      <HiOutlineScale
                        size={20}
                        className="text-skin-accent"
                        aria-hidden="true"
                      />

                      <span className="h-px w-9 bg-skin-accent/60" />
                    </div>

                    <h3
                      className="
                        card-title
                        line-clamp-2
                        text-skin-heading
                      "
                    >
                      {title}
                    </h3>

                    <p
                      className="
                        body-3 mt-4
                        line-clamp-3
                        text-skin-muted
                      "
                    >
                      {preview}
                    </p>

                    {/* Card footer */}
                    <div className="mt-auto pt-7">

                      <div className="mb-5 h-px bg-skin-border" />

                      <div
                        className="
                          flex items-center
                          justify-between gap-3
                        "
                      >
                        <span className="button-sm text-skin-heading">
                          {content.discover}
                        </span>

                        <span
                          className="
                            flex h-10 w-10
                            items-center justify-center

                            rounded-xl
                            border border-skin-border
                            bg-skin-soft

                            text-skin-heading
                            transition-colors duration-300

                            group-hover:border-brand-navy
                            group-hover:bg-brand-navy
                            group-hover:text-white

                            dark:group-hover:border-white/20
                            dark:group-hover:bg-white/15
                          "
                        >
                          <HiOutlineArrowUpRight
                            size={19}
                            aria-hidden="true"
                            className="directional-arrow"
                          />
                        </span>
                      </div>
                    </div>

                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

export default RelatedServices;
